<template>
  <div :class="backgroundColor">
    <h2 v-if="title">
      {{ title }}
    </h2>

    <div
      class="chart"
      :class="{
        'chart--no-title': !title,
        'chart--reversed': textAlign === 'textRight'
      }"
    >
      <div
        v-if="text"
        v-html="text"
      />

      <div v-if="chartData">
        <chart
          :type="chartType"
          :chart-data="chartData"
          :size="size"
        />

        <div
          v-if="footnote"
          class="chart-footnote"
        >
          {{ footnote }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import Chart from './Chart.vue'

  /**
   *We're using chartjs for these awesome graphs, and there are plenty more options/styles to be added if a need should arise:**
   *https://www.chartjs.org/docs/ **
   */

  export default {
    name: 'ZgCharts',
    components: { Chart },
    props: {
      /**
       *Main header above the paragraph**
       */
      title: {
        type: String,
        required: false,
        default: null
      },
      /**
       *Paragraph-text**
       */
      text: {
        type: String,
        required: false,
        default: null
      },
      /**
       *The type of chart to use:**
       *pie | horizontal-bar | horizontal-bar-stacked**
       */
      chartType: {
        type: String,
        required: false,
        default: 'textLeft'
      },
      /**
       *Object containing properties specific to the chart-component such as data and style**
       */
      chartData: {
        type: Object,
        required: false,
        default: () => {}
      },
      /**
       *Size of the chart**
       *normal | small | medium | large**
       */
      size: {
        type: String,
        required: false,
        default: 'medium'
      },
      /**
       *Size of the text underneath the chart**
       */
      footnote: {
        type: String,
        required: false,
        default: null
      },
      /**
       *Determines the position of the text-block in the containing div**
       *textLeft | textRight**
       */
      textAlign: {
        type: String,
        required: false,
        default: 'textLeft'
      },
      /**
       *Determines the background-color of the containing div**
       *Is set by using one of the implemented scss-classes:**
       *pink | white | background**
       */
      backgroundColor: {
        type: String,
        required: false,
        default: 'white'
      }
    }
  }
</script>

<style lang="scss" scoped>
  h2 {
    text-align: left;
    padding-top: rhythm();
    word-break: break-word;
  }

  .background {
    background: color(beige);
  }

  .white {
    background: color(white);
  }

  .pink {
    background: color(purple);
  }

  .chart {
    display: flex;
    align-items: top;
    text-align: left;
    width: 100%;
    flex-direction: column;
    gap: rhythm();

    & > div {
      flex: 1;
      width: 100%;
    }

    @include mq(large) {
      flex-direction: row;
    }

    ::v-deep ul {
      padding-left: unset;
      list-style: none;

      li {
        @include type-body-s;

        &:nth-child(n+2) {
          padding-top: rhythm(xsmall);
        }

        @include mq(large) {
          @include type-body-m;
        }
      }
    }

    ::v-deep a {
      word-break: break-all;
      border-bottom: unset;
      text-decoration: underline;
    }

    &--no-title {
      @include mq(large) {
        padding-top: rhythm();
      }
    }

    &--reversed {
      flex-direction: column-reverse;

      @include mq(large) {
        flex-direction: row-reverse;
      }
    }
  }

  .chart-footnote {
    @include type-body-xs;

    margin-top: rhythm(xsmall);
    margin-bottom: rhythm(xsmall);
    text-align: center;
  }
</style>
